import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useParsedIdsFromStrings } from '../../../../hooks/useParsedIdsFromStrings';
import { useSelectedCampaigns } from './useSelectedCampaigns';
import { useSuggestedCampaigns } from './useSuggestedCampaigns';
import { DEFAULT_TYPEAHEAD_QUERY_LIMIT } from 'modules/queryBuilder/constants';
import { localizeNumber } from 'utils/l10n';

type UseCampaignOptionsProps = {
  search: string;
  isRecurring?: boolean;
  values: string[] | null;
};

export const useCampaignOptions = ({
  search,
  isRecurring = false,
  values, // array of selected ids
}: UseCampaignOptionsProps) => {
  const { t } = useTranslation();
  const [selectedCampaignPage, setSelectedCampaignPage] = useState<number>(0); // total fetched selected campaign pages
  const [suggestedCampaignPage, setSuggestedCampaignPage] = useState<number>(0); // total fetched suggested campaign pages
  const [numSelectedCampaigns, setNumSelectedCampaigns] = useState<number>(values?.length ?? 0);

  const keepFetchingSelectedCampaigns = useMemo(() => {
    return (
      !!numSelectedCampaigns &&
      numSelectedCampaigns > (selectedCampaignPage + 1) * DEFAULT_TYPEAHEAD_QUERY_LIMIT.limit
    );
  }, [numSelectedCampaigns, selectedCampaignPage]);

  const {
    campaigns: suggestedCampaigns,
    campaignsCount,
    didError: didSuggestedCampaignsError,
    isLoading: isSuggestedCampaignsLoading,
  } = useSuggestedCampaigns(search, suggestedCampaignPage, isRecurring);

  const selectedIds = useParsedIdsFromStrings(values ?? []);
  const {
    campaigns: selectedCampaigns,
    campaignsCount: selectedCampaignsCount,
    didError: didSelectedCampaignsError,
    isLoading: isSelectedCampaignsLoading,
  } = useSelectedCampaigns(selectedIds, search, selectedCampaignPage, isRecurring);

  useEffect(() => {
    if (!isSelectedCampaignsLoading) {
      setNumSelectedCampaigns(selectedCampaignsCount);
    }
  }, [selectedCampaignsCount, isSelectedCampaignsLoading]);

  const options = useMemo(() => {
    // Don't return unless selcted campaigns have all been fetched
    if (keepFetchingSelectedCampaigns && !!numSelectedCampaigns) {
      return [];
    }
    return suggestedCampaigns
      .filter((campaign) => !values?.includes(campaign.id.toString()))
      .map((campaign) => ({
        title: campaign.name,
        subtitle: `${t('v2.conditionBuilder.campaignSubtitle.id', { id: campaign.id })}${campaign.expectedSendSize ? t('v2.conditionBuilder.campaignSubtitle.estSendSize', { estimatedSendSize: localizeNumber(campaign.expectedSendSize) }) : ''}`,
        value: campaign.id.toString(),
      }));
  }, [keepFetchingSelectedCampaigns, numSelectedCampaigns, suggestedCampaigns, t, values]);

  const selectedOptions = useMemo(
    () =>
      selectedCampaigns.map((campaign) => ({
        title: campaign.name,
        subtitle: `${t('v2.conditionBuilder.campaignSubtitle.id', { id: campaign.id })}${campaign.expectedSendSize ? t('v2.conditionBuilder.campaignSubtitle.estSendSize', { estimatedSendSize: localizeNumber(campaign.expectedSendSize) }) : ''}`,
        value: campaign.id.toString(),
      })),
    [t, selectedCampaigns],
  );

  const didError = didSuggestedCampaignsError || didSelectedCampaignsError;

  const canFetch = useMemo(() => {
    const areCampaignsStillLoading = isSuggestedCampaignsLoading || isSelectedCampaignsLoading;
    const areAllCampaignsFetched =
      campaignsCount <= (suggestedCampaignPage + 1) * DEFAULT_TYPEAHEAD_QUERY_LIMIT.limit;
    return !areCampaignsStillLoading && !areAllCampaignsFetched;
  }, [
    isSuggestedCampaignsLoading,
    isSelectedCampaignsLoading,
    suggestedCampaignPage,
    campaignsCount,
  ]);

  const onFetchMoreCampaigns = useCallback(() => {
    if (canFetch) {
      if (keepFetchingSelectedCampaigns) {
        setSelectedCampaignPage(selectedCampaignPage + 1);
      } else {
        setSuggestedCampaignPage(suggestedCampaignPage + 1);
      }
    }
  }, [keepFetchingSelectedCampaigns, selectedCampaignPage, suggestedCampaignPage, canFetch]);

  const resetCampaignPages = () => {
    setSelectedCampaignPage(0);
    setSuggestedCampaignPage(0);
  };

  return {
    options: options || [],
    selectedOptions: selectedOptions || [],
    didError,
    isSuggestedCampaignsLoading,
    isSelectedCampaignsLoading,
    suggestedCampaignPage,
    selectedCampaignPage,
    resetCampaignPages,
    campaignsCount,
    onFetchMoreCampaigns,
  };
};
